import { useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateTrialRoleRequest,
  getTrialRole,
  TRIAL_ROLE_CODES,
  TRIAL_ROLE_CONSTANTS,
  TrialRoleCodeSchema,
  type TrialRoleDto,
  type UpdateTrialRoleRequest,
} from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { AlertCircle, Lock, X } from 'lucide-react';
import { z } from 'zod';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

export interface RoleCatalogProps {
  trialId: number;
  /** personnel:write, passed from the cockpit. Independent of trials:write. */
  canWritePersonnel: boolean;
}

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

/** Fixed catalog in scenario order. Labels use the legacy shortcut (unCRA), values use the enum key. */
export const CATALOG_ROLE_OPTIONS = TRIAL_ROLE_CODES.map((roleCode) => {
  const constant = TRIAL_ROLE_CONSTANTS[roleCode];
  return {
    roleCode,
    name: constant.name,
    shortcut: constant.shortcut,
    siteStaff: constant.siteStaff,
    label: `${constant.name} (${constant.shortcut})`,
  };
});

export function roleSourceLabel(roleCode: TrialRoleDto['roleCode']): 'Katalog stały' | 'Wpis ręczny' {
  return roleCode === null ? 'Wpis ręczny' : 'Katalog stały';
}

export function siteStaffLabel(siteStaff: boolean): 'Tak' | 'Nie' {
  return siteStaff ? 'Tak' : 'Nie';
}

/**
 * Catalog role codes already assigned in the trial. The role picker in "Dodaj rolę w badaniu"
 * hides these so the same catalog role cannot be added twice.
 */
export function assignedCatalogRoleCodes(roles: readonly TrialRoleDto[]): string[] {
  return roles.flatMap((role) => (role.roleCode === null ? [] : [role.roleCode]));
}

export const CatalogRoleFormSchema = z.object({
  roleCode: TrialRoleCodeSchema,
  comments: z.string().max(10_000),
});

export type CatalogRoleFormInput = z.infer<typeof CatalogRoleFormSchema>;

export const ManualRoleFormSchema = z.object({
  name: z.string().trim().min(1, 'Nazwa roli jest wymagana').max(255),
  shortcut: z.string().trim().min(1, 'Skrót jest wymagany').max(16),
  siteStaff: z.boolean(),
  comments: z.string().max(10_000),
});

export type ManualRoleFormInput = z.infer<typeof ManualRoleFormSchema>;

const MANUAL_ROLE_CODE = '__manual__' as const;

const AddRoleFormSchema = z
  .object({
    roleCode: z.union([TrialRoleCodeSchema, z.literal(MANUAL_ROLE_CODE)]),
    name: z.string().max(255),
    shortcut: z.string().max(16),
    siteStaff: z.boolean(),
    comments: z.string().max(10_000),
  })
  .superRefine((data, ctx) => {
    if (data.roleCode === MANUAL_ROLE_CODE) {
      if (data.name.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['name'], message: 'Nazwa roli jest wymagana' });
      }
      if (data.shortcut.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['shortcut'], message: 'Skrót jest wymagany' });
      }
    }
  });

type AddRoleFormInput = z.infer<typeof AddRoleFormSchema>;

/** Catalog add copies name, shortcut and site staff on the server. The client sends the code and an optional comment. */
export function toCreateCatalogRoleRequest(
  trialId: number,
  data: CatalogRoleFormInput,
): CreateTrialRoleRequest {
  const comments = data.comments.trim();
  const request: CreateTrialRoleRequest = {
    trialId,
    roleCode: data.roleCode,
  };
  if (comments.length > 0) {
    request.comments = comments;
  }
  return request;
}

/** Manual add sends the typed name, shortcut and site-staff flag. There is no catalog code. */
export function toCreateManualRoleRequest(
  trialId: number,
  data: ManualRoleFormInput,
): CreateTrialRoleRequest {
  const comments = data.comments.trim();
  const request: CreateTrialRoleRequest = {
    trialId,
    name: data.name.trim(),
    shortcut: data.shortcut.trim(),
    siteStaff: data.siteStaff,
  };
  if (comments.length > 0) {
    request.comments = comments;
  }
  return request;
}

/** Manual-role edit sends the four editable fields; an empty comment clears it. */
export function toUpdateManualRoleRequest(data: ManualRoleFormInput): UpdateTrialRoleRequest {
  const comments = data.comments.trim();
  return {
    name: data.name.trim(),
    shortcut: data.shortcut.trim(),
    siteStaff: data.siteStaff,
    comments: comments.length > 0 ? comments : null,
  };
}

const CATALOG_ROLE_LABEL = 'Rola z katalogu stałego';

const columnHelper = createAppColumnHelper<TrialRoleDto>();

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

export function RoleCatalog({ trialId, canWritePersonnel }: RoleCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<TrialRoleDto | null>(null);
  const [deletingRole, setDeletingRole] = useState<TrialRoleDto | null>(null);

  const rolesQuery = useQuery({
    queryKey: trialQueryKeys.roles(trialId),
    queryFn: () => trialsApi.listRoles(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const roles = rolesQuery.data ?? [];
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('name', {
          header: 'Nazwa',
          cell: (info) => (
            <span className="inline-flex items-center gap-1.5">
              {info.row.original.roleCode !== null && (
                <Lock className="h-3.5 w-3.5 text-slate-400" aria-label={CATALOG_ROLE_LABEL} />
              )}
              {info.getValue()}
            </span>
          ),
        }),
        columnHelper.accessor('shortcut', { header: 'Skrót' }),
        columnHelper.accessor('siteStaff', {
          header: 'Personel ośrodka',
          cell: (info) => siteStaffLabel(info.getValue()),
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => {
            const role = info.row.original;
            const comment = role.comments?.trim();
            return (
              <div className="flex items-center justify-between gap-2">
                <span>{comment && comment.length > 0 ? comment : '—'}</span>
                {canWritePersonnel && role.roleCode === null && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingRole(role)}
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                    >
                      Edytuj
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingRole(role)}
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-rose-300 hover:bg-slate-800"
                    >
                      Usuń
                    </button>
                  </div>
                )}
              </div>
            );
          },
        }),
      ]),
    [canWritePersonnel],
  );
  const table = useAppTable({ data: roles, columns });

  const handleCreated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.roles(trialId) });
    setAddRoleOpen(false);
  };

  const handleUpdated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.roles(trialId) });
    setEditingRole(null);
  };

  const handleDeleted = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.roles(trialId) });
    setDeletingRole(null);
  };

  return (
    <section aria-label="Role w badaniu" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <h3 className="text-sm font-semibold text-slate-100">Role w badaniu</h3>
      <p className="mt-1 text-xs text-slate-400">
        Role opisują funkcję w protokole. Nie zmieniają uprawnień do programu.
      </p>

      {rolesQuery.isLoading ? (
        <p className="py-6 text-sm text-slate-400">Ładowanie ról w badaniu</p>
      ) : rolesQuery.isError ? (
        <div role="alert" className="mt-3 text-sm text-rose-300">
          Nie udało się wczytać ról w badaniu.
          <button type="button" onClick={() => void rolesQuery.refetch()} className="ml-3 underline">
            Spróbuj ponownie
          </button>
        </div>
      ) : roles.length === 0 ? (
        <p className="mt-3 text-xs text-slate-400">Brak ról w badaniu</p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/80">
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 text-slate-200">
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWritePersonnel && (
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setAddRoleOpen(true)}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
          >
            Dodaj rolę
          </button>
        </div>
      )}

      {addRoleOpen && (
        <AddRoleDialog
          trialId={trialId}
          existingRoleCodes={assignedCatalogRoleCodes(roles)}
          onClose={() => setAddRoleOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {editingRole !== null && (
        <EditManualRoleDialog
          trialId={trialId}
          role={editingRole}
          onClose={() => setEditingRole(null)}
          onSaved={handleUpdated}
        />
      )}
      {deletingRole !== null && (
        <DeleteManualRoleDialog
          trialId={trialId}
          role={deletingRole}
          onClose={() => setDeletingRole(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
}

function LockedCatalogFields({ roleCode }: { roleCode: string }): JSX.Element {
  const selected = getTrialRole(roleCode);
  return (
    <>
      <div>
        <label htmlFor="catalog-role-name" className={labelClass}>Nazwa</label>
        <input
          id="catalog-role-name"
          value={selected?.name ?? ''}
          disabled
          readOnly
          className={fieldClass}
        />
      </div>
      <div>
        <label htmlFor="catalog-role-shortcut" className={labelClass}>Skrót</label>
        <input
          id="catalog-role-shortcut"
          value={selected?.shortcut ?? ''}
          disabled
          readOnly
          className={fieldClass}
        />
      </div>
      <div>
        <label htmlFor="catalog-role-site-staff" className={labelClass}>Personel ośrodka</label>
        <input
          id="catalog-role-site-staff"
          value={selected ? siteStaffLabel(selected.siteStaff) : ''}
          disabled
          readOnly
          className={fieldClass}
        />
      </div>
    </>
  );
}

export function AddRoleDialog({
  trialId,
  existingRoleCodes = [],
  onClose,
  onCreated,
}: {
  trialId: number;
  /** Catalog role codes already assigned in this trial; they are hidden from the picker (B.3). */
  existingRoleCodes?: readonly string[];
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const availableOptions = useMemo(() => {
    const assigned = new Set(existingRoleCodes);
    return CATALOG_ROLE_OPTIONS.filter((option) => !assigned.has(option.roleCode));
  }, [existingRoleCodes]);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AddRoleFormInput>({
    resolver: zodResolver(AddRoleFormSchema),
    defaultValues: {
      roleCode: availableOptions[0]?.roleCode ?? MANUAL_ROLE_CODE,
      name: '',
      shortcut: '',
      siteStaff: true,
      comments: '',
    },
  });
  const roleCode = watch('roleCode');
  const isManual = roleCode === MANUAL_ROLE_CODE;

  const onSubmit = async (data: AddRoleFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const request =
        data.roleCode === MANUAL_ROLE_CODE
          ? toCreateManualRoleRequest(trialId, data)
          : toCreateCatalogRoleRequest(trialId, { roleCode: data.roleCode, comments: data.comments });
      await trialsApi.createRole(trialId, request);
      onCreated();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać roli.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-role-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="add-role-title" className="text-base font-semibold text-slate-100">
              Dodaj rolę w badaniu
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Wybierz rolę z katalogu stałego albo wpisz własną. Role z katalogu mają zablokowaną nazwę, skrót i oznaczenie „personel ośrodka”.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Zamknij" className="rounded-md p-1.5 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="add-role-select" className={labelClass}>Rola</label>
            <select id="add-role-select" disabled={isSubmitting} className={fieldClass} {...register('roleCode')}>
              {availableOptions.map((option) => (
                <option key={option.roleCode} value={option.roleCode}>{option.label}</option>
              ))}
              <option value={MANUAL_ROLE_CODE}>Wpisz rolę ręcznie…</option>
            </select>
          </div>

          {isManual ? (
            <>
              <div>
                <label htmlFor="add-role-name" className={labelClass}>Nazwa roli</label>
                <input id="add-role-name" type="text" disabled={isSubmitting} className={fieldClass} {...register('name')} />
                {errors.name && <p className="mt-1 text-xs text-rose-400">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="add-role-shortcut" className={labelClass}>Skrót</label>
                <input id="add-role-shortcut" type="text" disabled={isSubmitting} className={fieldClass} {...register('shortcut')} />
                {errors.shortcut && <p className="mt-1 text-xs text-rose-400">{errors.shortcut.message}</p>}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" disabled={isSubmitting} {...register('siteStaff')} />
                Personel ośrodka
              </label>
            </>
          ) : (
            <LockedCatalogFields roleCode={roleCode} />
          )}

          <div>
            <label htmlFor="add-role-comments" className={labelClass}>Komentarz (opcjonalnie)</label>
            <input
              id="add-role-comments"
              type="text"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('comments')}
            />
            {errors.comments && <p className="mt-1 text-xs text-rose-400">{errors.comments.message}</p>}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50">
              {isSubmitting ? 'Zapisywanie…' : 'Dodaj rolę'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditManualRoleDialog({
  trialId,
  role,
  onClose,
  onSaved,
}: {
  trialId: number;
  role: TrialRoleDto;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualRoleFormInput>({
    resolver: zodResolver(ManualRoleFormSchema),
    defaultValues: {
      name: role.name,
      shortcut: role.shortcut,
      siteStaff: role.siteStaff,
      comments: role.comments ?? '',
    },
  });

  const onSubmit = async (data: ManualRoleFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.updateRole(trialId, role.id, toUpdateManualRoleRequest(data));
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać roli.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-manual-role-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="edit-manual-role-title" className="text-base font-semibold text-slate-100">
              Edytuj rolę wpisaną ręcznie
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Dla roli ręcznej edytowalne są nazwa, skrót i oznaczenie „personel ośrodka”.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Zamknij" className="rounded-md p-1.5 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="edit-manual-role-name" className={labelClass}>Nazwa roli</label>
            <input id="edit-manual-role-name" type="text" disabled={isSubmitting} className={fieldClass} {...register('name')} />
            {errors.name && <p className="mt-1 text-xs text-rose-400">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="edit-manual-role-shortcut" className={labelClass}>Skrót</label>
            <input id="edit-manual-role-shortcut" type="text" disabled={isSubmitting} className={fieldClass} {...register('shortcut')} />
            {errors.shortcut && <p className="mt-1 text-xs text-rose-400">{errors.shortcut.message}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={isSubmitting} {...register('siteStaff')} />
            Personel ośrodka
          </label>

          <div>
            <label htmlFor="edit-manual-role-comments" className={labelClass}>Komentarz (opcjonalnie)</label>
            <input id="edit-manual-role-comments" type="text" disabled={isSubmitting} className={fieldClass} {...register('comments')} />
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50">
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteManualRoleDialog({
  trialId,
  role,
  onClose,
  onDeleted,
}: {
  trialId: number;
  role: TrialRoleDto;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async (): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.deleteRole(trialId, role.id);
      onDeleted();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć roli.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-manual-role-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <h2 id="delete-manual-role-title" className="text-base font-semibold text-slate-100">
          Czy na pewno usunąć rolę „{role.name}”?
        </h2>
        <p className="mt-2 text-xs text-slate-400">
          Usunąć można tylko rolę wpisaną ręcznie, która nie jest przypisana do personelu badania.
        </p>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => {
              void handleDelete();
            }}
            disabled={isSubmitting}
            className="rounded-md bg-rose-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Usuwanie…' : 'Usuń rolę'}
          </button>
        </div>
      </div>
    </div>
  );
}
